
import React from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";

const AboutUs = () => {
  const isMobile = useIsMobile();
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      title: "عن مجموعة الفنون",
      aboutTitle: "نبذة عنا",
      aboutParagraphs: [
        "تأسست مجموعة الفنون للصناعة في المملكة العربية السعودية بهدف تقديم حلول متكاملة في مجالات التصنيع والتصميم. على مر السنين، نجحنا في بناء سمعة قوية كشركة رائدة تجمع بين الإبداع والجودة العالية والالتزام بتلبية احتياجات العملاء.",
        "نؤمن في مجموعة الفنون بأن كل مشروع هو فرصة لإظهار التميز. لذلك نحرص على تقديم منتجات وخدمات تتجاوز توقعات عملائنا، معتمدين على فريق متخصص من المصممين والحرفيين والفنيين ذوي الخبرة العالية."
      ],
      areasTitle: "مجالات عملنا",
      areas: [
        {
          title: "صناعة الأخشاب",
          description: "نقدم تصاميم مبتكرة ومنتجات خشبية عالية الجودة تجمع بين الجمال والمتانة، بدءًا من الأثاث المنزلي وحتى التجهيزات التجارية المتكاملة."
        },
        {
          title: "تصنيع الألمنيوم",
          description: "نتخصص في تصنيع منتجات الألمنيوم بمواصفات عالية ودقة متناهية، من النوافذ والأبواب إلى الواجهات والهياكل الخاصة."
        },
        {
          title: "أعمال الحديد",
          description: "نقدم مجموعة واسعة من أعمال الحديد المصممة بدقة، بدءًا من البوابات والدرابزينات وصولاً إلى الهياكل المعدنية المعقدة."
        }
      ],
      visionMissionTitle: "رؤيتنا ورسالتنا",
      vision: {
        title: "رؤيتنا",
        description: "أن نكون الشركة الرائدة في مجال التصنيع والتصميم في المملكة العربية السعودية والشرق الأوسط، معروفين بالابتكار والجودة العالية والتميز في خدمة العملاء."
      },
      mission: {
        title: "رسالتنا",
        description: "تقديم منتجات وخدمات متميزة تلبي احتياجات وتطلعات عملائنا، مع الالتزام بمعايير الجودة العالية والإبداع والاستدامة في كل ما نقوم به."
      },
      valuesTitle: "قيمنا",
      values: [
        { bold: "الجودة:", text: "نلتزم بتقديم أعلى مستويات الجودة في كل منتج وخدمة نقدمها." },
        { bold: "الإبداع:", text: "نسعى دائماً لتقديم حلول مبتكرة ومتميزة تلبي احتياجات عملائنا وتتجاوز توقعاتهم." },
        { bold: "النزاهة:", text: "نعمل بشفافية ومصداقية في جميع تعاملاتنا مع العملاء والموردين والشركاء." },
        { bold: "الالتزام:", text: "نلتزم بالمواعيد النهائية وبتحقيق رضا العملاء في كل مشروع نقوم به." },
        { bold: "العمل الجماعي:", text: "نؤمن بأهمية العمل كفريق واحد لتحقيق أهدافنا المشتركة وتقديم أفضل النتائج." }
      ]
    },
    en: {
      title: "About ALFUNOON Group",
      aboutTitle: "About Us",
      aboutParagraphs: [
        "ALFUNOON Industries Group was established in Saudi Arabia with the aim of providing integrated solutions in manufacturing and design. Over the years, we have successfully built a strong reputation as a leading company that combines creativity, high quality, and commitment to meeting customer needs.",
        "At ALFUNOON Group, we believe that every project is an opportunity to demonstrate excellence. Therefore, we are committed to providing products and services that exceed our customers' expectations, relying on a specialized team of highly experienced designers, craftsmen, and technicians."
      ],
      areasTitle: "Our Areas of Work",
      areas: [
        {
          title: "Wood Manufacturing",
          description: "We offer innovative designs and high-quality wood products that combine beauty and durability, from home furniture to comprehensive commercial fixtures."
        },
        {
          title: "Aluminum Fabrication",
          description: "We specialize in manufacturing aluminum products with high specifications and precision, from windows and doors to facades and special structures."
        },
        {
          title: "Iron Works",
          description: "We offer a wide range of precisely designed iron works, from gates and railings to complex metal structures."
        }
      ],
      visionMissionTitle: "Our Vision & Mission",
      vision: {
        title: "Our Vision",
        description: "To be the leading company in manufacturing and design in Saudi Arabia and the Middle East, known for innovation, high quality, and excellence in customer service."
      },
      mission: {
        title: "Our Mission",
        description: "To provide distinguished products and services that meet the needs and aspirations of our customers, while adhering to high quality standards, creativity, and sustainability in everything we do."
      },
      valuesTitle: "Our Values",
      values: [
        { bold: "Quality:", text: "We are committed to delivering the highest levels of quality in every product and service we offer." },
        { bold: "Creativity:", text: "We always strive to provide innovative and distinguished solutions that meet our customers' needs and exceed their expectations." },
        { bold: "Integrity:", text: "We work with transparency and credibility in all our dealings with customers, suppliers, and partners." },
        { bold: "Commitment:", text: "We are committed to deadlines and achieving customer satisfaction in every project we undertake." },
        { bold: "Teamwork:", text: "We believe in the importance of working as one team to achieve our common goals and deliver the best results." }
      ]
    }
  };

  const t = content[language];
  
  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-primary to-black relative"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className={`relative z-10 ${isMobile ? "pt-16" : ""}`}>
        <Navbar />
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-xl"
          >
            <h1 className="text-5xl font-kufi font-bold mb-12 text-center">{t.title}</h1>
            
            <section className="mb-16">
              <h2 className="text-3xl font-kufi font-bold mb-6 text-primary">{t.aboutTitle}</h2>
              <div className="space-y-6 text-lg text-gray-700 font-kufi leading-relaxed">
                {t.aboutParagraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </section>
            
            <section className="mb-16">
              <h2 className="text-3xl font-kufi font-bold mb-6 text-primary">{t.areasTitle}</h2>
              <div className="grid md:grid-cols-3 gap-8 mt-8">
                {t.areas.map((area, index) => (
                  <motion.div 
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white p-6 rounded-lg shadow-md"
                  >
                    <h3 className="text-2xl font-kufi font-bold mb-4 text-center">{area.title}</h3>
                    <p className="text-gray-700 font-kufi">{area.description}</p>
                  </motion.div>
                ))}
              </div>
            </section>
            
            <section className="mb-16">
              <h2 className="text-3xl font-kufi font-bold mb-6 text-primary">{t.visionMissionTitle}</h2>
              <div className="grid md:grid-cols-2 gap-12">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-2xl font-kufi font-bold mb-4 text-center">{t.vision.title}</h3>
                  <p className="text-gray-700 font-kufi">{t.vision.description}</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-2xl font-kufi font-bold mb-4 text-center">{t.mission.title}</h3>
                  <p className="text-gray-700 font-kufi">{t.mission.description}</p>
                </div>
              </div>
            </section>
            
            <section>
              <h2 className="text-3xl font-kufi font-bold mb-6 text-primary">{t.valuesTitle}</h2>
              <div className="space-y-6 text-lg text-gray-700 font-kufi leading-relaxed">
                <ul className={`${isRTL ? 'list-disc list-inside' : 'list-disc list-inside'} space-y-4`}>
                  {t.values.map((value, index) => (
                    <li key={index}><span className="font-bold">{value.bold}</span> {value.text}</li>
                  ))}
                </ul>
              </div>
            </section>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AboutUs;
